import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getGraphQLClient } from '../lib/graphql';

interface UseAiSuggestionsOptions {
  /** GraphQL mutation string */
  fetchMutation: string;
  /** Variables passed to the mutation */
  fetchVariables: Record<string, unknown>;
  /** Field name in the mutation response containing the data */
  responseField: string;
  /** Number of suggestions to display at once */
  displayCount?: number;
  /** Total number of suggestions to fetch per batch */
  fetchCount?: number;
  /** Controls when the initial fetch happens */
  enabled?: boolean;
}

interface UseAiSuggestionsReturn {
  suggestions: string[];
  isLoading: boolean;
  refresh: () => void;
  error: string;
}

export default function useAiSuggestions({
  fetchMutation,
  fetchVariables,
  responseField,
  displayCount = 3,
  fetchCount = 15,
  enabled = false,
}: UseAiSuggestionsOptions): UseAiSuggestionsReturn {
  const { t } = useTranslation();
  const [cachedSuggestions, setCachedSuggestions] = useState<string[]>([]);
  const [displayedSuggestions, setDisplayedSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const hasFetched = useRef(false);

  const fetchSuggestions = useCallback(async (): Promise<string[]> => {
    const client = getGraphQLClient();
    const variables = { ...fetchVariables, count: fetchCount };

    const data = await client.request<Record<string, { suggestions: string[]; errors: string[] }>>(
      fetchMutation,
      variables
    );

    const response = data[responseField];
    if (response.errors?.length > 0) {
      setError(response.errors[0]);
      return [];
    }

    return response.suggestions || [];
  }, [fetchMutation, fetchVariables, responseField, fetchCount]);

  const loadSuggestions = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      if (cachedSuggestions.length >= displayCount) {
        const nextBatch = cachedSuggestions.slice(0, displayCount);
        setCachedSuggestions(cachedSuggestions.slice(displayCount));
        setDisplayedSuggestions(nextBatch);
      } else {
        const newSuggestions = await fetchSuggestions();
        if (newSuggestions.length > 0) {
          const nextBatch = newSuggestions.slice(0, displayCount);
          setCachedSuggestions(newSuggestions.slice(displayCount));
          setDisplayedSuggestions(nextBatch);
        }
      }
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
      setError(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  }, [cachedSuggestions, displayCount, fetchSuggestions, t]);

  // Fetch when enabled becomes true (initial load)
  useEffect(() => {
    if (enabled && !hasFetched.current && displayedSuggestions.length === 0) {
      hasFetched.current = true;
      loadSuggestions();
    }
  }, [enabled, displayedSuggestions.length, loadSuggestions]);

  // Reset when disabled
  useEffect(() => {
    if (!enabled) {
      hasFetched.current = false;
      setDisplayedSuggestions([]);
      setCachedSuggestions([]);
      setError('');
    }
  }, [enabled]);

  const refresh = useCallback(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  return {
    suggestions: displayedSuggestions,
    isLoading,
    refresh,
    error,
  };
}
